import axios from "axios";
import { API_BASE_URL, getAuthToken } from "./shared";

// API helpers for the Music Wallet (Artist Studio). Amounts are integers in minor units (cents) plus a currency code; the
// page formats them (walletFormat.js). The artist is always the signed-in user: nothing here sends an artist id.
const url = (path = "") => `${API_BASE_URL}/v1/artist-wallet${path}`;
const config = (extra = {}) => ({
  headers: { token: getAuthToken(), "Content-Type": "application/json" },
  timeout: 20000,
  ...extra,
});
const result = (response) => response.data?.result;

export const walletApi = {
  wallet: () => axios.get(url(), config()).then(result),
  // params: q, type, cursor, limit (the first page is 5 rows).
  transactions: (params = {}) => axios.get(url("/transactions"), config({ params })).then(result),
  payouts: (params = {}) => axios.get(url("/payouts"), config({ params })).then(result),
  // Monthly streaming royalty statements: the list (params: limit) and one month ("2026-09") with the amount per track.
  royaltyStatements: (params = {}) => axios.get(url("/royalty-statements"), config({ params })).then(result),
  royaltyStatement: (month) => axios.get(url(`/royalty-statements/${encodeURIComponent(month)}`), config()).then(result),
  // Monthly account statements: the list (params: limit), one month ("2026-09") with every kind of money movement, and the CSV file
  // of that month (a Blob; the token travels in the request header, so the file is fetched here and then saved by the page).
  statements: (params = {}) => axios.get(url("/statements"), config({ params })).then(result),
  statement: (month) => axios.get(url(`/statements/${encodeURIComponent(month)}`), config()).then(result),
  downloadStatement: (month) => axios.get(url(`/statements/${encodeURIComponent(month)}/download`), config({ responseType: "blob", timeout: 60000 })).then((r) => r.data),
  // body: { amount_minor, currency, idempotency_key }. The same key sent again returns the same payout, never a second one.
  withdraw: (body) => axios.post(url("/withdrawals"), body, config({ timeout: 30000 })).then(result),
  // Payout method and schedule. The bank account itself is only ever handled by Stripe; Pipaan keeps the Stripe account id and
  // the last four digits.
  // The tax form the artist needs and where it stands; submitTaxForm body: { document_url, tax_form? } puts it in review.
  taxForm: () => axios.get(url("/tax-form"), config()).then(result),
  submitTaxForm: (body) => axios.post(url("/tax-form"), body, config()).then(result),
  payoutSettings: () => axios.get(url("/payout-settings"), config()).then(result),
  // body: { schedule: "WEEKLY" | "MONTHLY", minimum_threshold_minor }.
  savePayoutSettings: (body) => axios.put(url("/payout-settings"), body, config()).then(result),
  // Emails a 6-digit code (re-authentication). In a test environment the answer also carries test_otp.
  sendPayoutOtp: () => axios.post(url("/payout-method/otp"), {}, config()).then(result),
  // body: { password } or { otp }. Answers { grant }, good for one change.
  reauthPayoutMethod: (body) => axios.post(url("/payout-method/reauth"), body, config()).then(result),
  // body: { grant }. Answers { url, simulated }: where Stripe's own page is (url is null in a simulated environment).
  startPayoutMethodChange: (grant) => axios.post(url("/payout-method/change"), { grant }, config()).then(result),
  // Asks the server to read the Stripe account again (never trusts the redirect). Simulated: { simulated_last4 } or { cancelled: true }.
  verifyPayoutMethodChange: (body = {}) => axios.post(url("/payout-method/verify"), body, config({ timeout: 30000 })).then(result),
};

export const WALLET_MESSAGES = {
  load: "We couldn't load your wallet. Please try again.",
  transactions: "We couldn't load your transactions. Please try again.",
  payouts: "We couldn't load your payout history. Please try again.",
  royaltyStatements: "We couldn't load your royalty statements. Please try again.",
  royaltyStatement: "We couldn't load that statement. Please try again.",
  statements: "We couldn't load your monthly statements. Please try again.",
  statement: "We couldn't load that statement. Please try again.",
  statementDownload: "We couldn't prepare the download. Please try again.",
  withdrawUnknown:
    "We couldn't confirm your withdrawal. It may still be processing. Press Withdraw again to check: the same request can't create a second payout.",
  withdrawFailed: "Your withdrawal could not be requested. Please try again.",
  accountLink: "The payout account page could not be opened. Please try again.",
  payoutSettingsLoad: "We couldn't load your payout settings. Please try again.",
  payoutSettingsSave: "We couldn't save your payout settings. Please try again.",
  payoutVerify: "We couldn't check your payout method with Stripe. Please try again.",
  reauth: "We couldn't confirm it's you. Please try again.",
  otpSend: "We couldn't send the code. Please try again.",
  taxLoad: "We couldn't load your tax information. Please try again.",
  taxSubmit: "We couldn't send your tax form. Please try again.",
};

// One sentence for the artist from a failed request: the server's own wording for the rules it enforces.
export function walletErrorMessage(error, fallback) {
  const response = error?.response;
  if (!response) return fallback;
  if (response.status === 401) return "Your session has expired. Please log in again.";
  if (response.status >= 500 && !response.data?.code) return fallback;
  return response.data?.message || fallback;
}
